import type {
  ConnectorRestrictionRuleResponse,
  CreateConnectorRestrictionRuleRequest,
  ListConnectorRestrictionRulesQuery,
  UpdateConnectorRestrictionRuleRequest,
} from '../types';
import type { RequestFn } from '../../client';

const BASE = '/admin/connector-restriction-rules';

/**
 * Per-shop / per-project connector restriction rules.
 *
 * Distinct from `connectorRestrictions` (the merchant-tier attach gate):
 * these rules allow/deny a connector for one shop (`scope: 'profile'`) or
 * project (`scope: 'project'`) at routing time. A `deny` is never routed even
 * when the connector is attached and enabled; once a scope has any `allow` it
 * becomes a whitelist, and the project scope takes precedence over the shop
 * scope. Admin-only, under `/admin/connector-restriction-rules`; exposed only
 * via `'@delopay/sdk/internal'`.
 */
export class ConnectorRestrictionRules {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create one allow/deny rule. A duplicate
   * `(merchant_id, scope, scope_id, connector)` is rejected — update or delete
   * the existing rule instead.
   */
  async create(
    body: CreateConnectorRestrictionRuleRequest,
  ): Promise<ConnectorRestrictionRuleResponse> {
    return this.request('POST', BASE, { body });
  }

  /**
   * List rules for one scope (`scope` + `scope_id`) or a whole merchant
   * (`merchant_id`). Pass exactly one selector.
   */
  async list(
    query: ListConnectorRestrictionRulesQuery,
  ): Promise<ConnectorRestrictionRuleResponse[]> {
    // Spread into a fresh object literal so the typed query satisfies the
    // request layer's `Record<string, …>` index signature. `undefined` values
    // are dropped by `request()`.
    return this.request('GET', BASE, { query: { ...query } });
  }

  /** Retrieve a single rule by ID. */
  async retrieve(id: string): Promise<ConnectorRestrictionRuleResponse> {
    return this.request('GET', `${BASE}/${encodeURIComponent(id)}`);
  }

  /** Update a rule's action and/or reason. */
  async update(
    id: string,
    body: UpdateConnectorRestrictionRuleRequest,
  ): Promise<ConnectorRestrictionRuleResponse> {
    return this.request('PATCH', `${BASE}/${encodeURIComponent(id)}`, { body });
  }

  /** Delete a rule by ID. */
  async delete(id: string): Promise<{ id: string; deleted: boolean }> {
    return this.request('DELETE', `${BASE}/${encodeURIComponent(id)}`);
  }
}
